import * as React from "react";

type Ctx = {
  value?: string;
  setValue: (v: string) => void;
  open: boolean;
  setOpen: (o: boolean) => void;
};
const SelectCtx = React.createContext<Ctx | null>(null);

type RootProps = {
  value?: string;
  onValueChange?: (v: string) => void;
  children: React.ReactNode;
};
export function Select({ value, onValueChange, children }: RootProps) {
  const [internal, setInternal] = React.useState(value);
  React.useEffect(() => setInternal(value), [value]);
  const [open, setOpen] = React.useState(false);

  const setValue = (v: string) => {
    setInternal(v);
    onValueChange?.(v);
    setOpen(false);
  };

  return (
    <span className="relative inline-block">
      <SelectCtx.Provider value={{ value: internal, setValue, open, setOpen }}>
        {children}
      </SelectCtx.Provider>
    </span>
  );
}

type TriggerProps = React.ButtonHTMLAttributes<HTMLButtonElement> & { asChild?: boolean };
export const SelectTrigger = React.forwardRef<HTMLButtonElement, TriggerProps>(
  ({ asChild, className = "", children, ...rest }, ref) => {
    const ctx = React.useContext(SelectCtx)!;
    const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      ctx.setOpen(!ctx.open);
      rest.onClick?.(e);
    };
    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children as any, {
        onClick,
        className: ((children as any).props?.className || "") + " " + className,
      });
    }
    return (
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        className={
          "inline-flex items-center justify-between rounded-md border px-3 h-9 w-[200px] " +
          "bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-sm " +
          className
        }
        {...rest}
      >
        {children}
      </button>
    );
  }
);
SelectTrigger.displayName = "SelectTrigger";

export function SelectValue({ placeholder }: { placeholder?: string }) {
  const ctx = React.useContext(SelectCtx)!;
  const labelMap: Record<string, string> = {
    coingecko: "CoinGecko",
    coinbase: "Coinbase",
    kraken: "Kraken",
    all: "All (Merged)",
  };
  const text = (ctx.value && (labelMap[ctx.value] || ctx.value)) || placeholder || "";
  return <span className="truncate">{text}</span>;
}

type ContentProps = React.HTMLAttributes<HTMLDivElement>;
export function SelectContent({ className = "", children, ...rest }: ContentProps) {
  const ctx = React.useContext(SelectCtx)!;
  if (!ctx.open) return null;
  return (
    <div
      className={
        "absolute z-50 mt-1 w-[240px] rounded-md border bg-white dark:bg-zinc-900 " +
        "border-zinc-200 dark:border-zinc-700 shadow-lg " +
        className
      }
      onMouseDown={(e) => e.preventDefault()}
      {...rest}
    >
      <div className="max-h-80 overflow-auto py-1">{children}</div>
    </div>
  );
}

type ItemProps = React.HTMLAttributes<HTMLDivElement> & { value: string };
export function SelectItem({ value, className = "", children, ...rest }: ItemProps) {
  const ctx = React.useContext(SelectCtx)!;
  const active = ctx.value === value;
  return (
    <div
      role="option"
      aria-selected={active}
      tabIndex={0}
      onClick={() => ctx.setValue(value)}
      className={
        "px-3 py-2 cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/60 " +
        (active ? "bg-zinc-50 dark:bg-zinc-800/60 " : "") +
        className
      }
      {...rest}
    >
      {children}
    </div>
  );
}
